/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from 'react';
import FormWrapper from './FormWrapper';
import ChallengeNameInput from './ChallengeNameInput';
import HideOnLowWidth from '@/components/HideOnLowWidth';
import CancelButton from '@/components/CancelButton';
import { IconButton } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import ChallengeContext from '../../ChallengeContext';
import useApi from '@/lib/api/useApi';
import ChallengePortfolioContext from '@/components/ChallengePortfolio/ChallengePortfolioContext';
import { type ChallengeWithoutRelations } from '@/lib/types';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import NewlySharedContext from '@/components/ChallengePortfolio/NormalView/NewlySharedChallenges/NewlySharedContext';
import useBlockPortfolioItemCloseWhenDirty from '../../../../portfolio-item/useBlockPortfolioItemCloseWhenDirty';

type ChallengeNameEditorProps = {
  onClose: () => void;
};

const ChallengeNameEditor = ({ onClose }: ChallengeNameEditorProps) => {
  const api = useApi();

  const [isNewlyShared, , setNewlySharedChallenges] =
    useContext(NewlySharedContext);
  const [, setChallengePortfolio] = useContext(ChallengePortfolioContext);
  const [challenge, setChallenge] = useContext(ChallengeContext);

  const [
    newChallengeName,
    setNewChallengeName,
    challengeNameHasChanged,
    reenableNewChallengeNameSync,
  ] = useComplexSwrState(challenge.name);
  const [saving, setSaving] = useState(false);

  const saveNameChange = useCallback(async (): Promise<void> => {
    if (newChallengeName.trim().length === 0) {
      return;
    }

    setSaving(true);

    const updatedChallenge = await api.put<ChallengeWithoutRelations>(
      `challenge-portfolio/challenges/${challenge.id}/name`,
      {
        name: newChallengeName.trim(),
      },
    );

    setChallenge(challenge => {
      challenge.name = updatedChallenge.name;
    });

    if (isNewlyShared) {
      // TODO newlyshared section should set up ChallengePortfolioContext
      // with newlysharedchallenges to avoid this hassle
      setNewlySharedChallenges(newlySharedChallenges => {
        newlySharedChallenges[challenge.id].name = updatedChallenge.name;
      });
    } else {
      setChallengePortfolio(challengePortfolio => {
        challengePortfolio.challenges[challenge.id].name =
          updatedChallenge.name;
      });
    }

    setSaving(false);

    onClose();

    reenableNewChallengeNameSync();
  }, [newChallengeName]);

  const handleFormSubmit = (e: React.SubmitEvent<HTMLFormElement>): void => {
    e.preventDefault();

    saveNameChange();
  };

  const cancelNameChange = () => {
    onClose();

    setNewChallengeName(challenge.name);
    reenableNewChallengeNameSync();
  };

  useBlockPortfolioItemCloseWhenDirty(challengeNameHasChanged, saveNameChange);

  return (
    <FormWrapper onSubmit={handleFormSubmit}>
      <ChallengeNameInput
        value={newChallengeName}
        onChange={e => setNewChallengeName(e.target.value)}
        onEscPress={cancelNameChange}
        autoFocus
      />
      <HideOnLowWidth width={700}>
        <CancelButton
          visible={newChallengeName !== challenge.name}
          onClick={cancelNameChange}
        />
      </HideOnLowWidth>
      <IconButton
        type="submit"
        disabled={newChallengeName.trim().length === 0 || saving}
      >
        <CheckIcon />
      </IconButton>
    </FormWrapper>
  );
};

export default ChallengeNameEditor;
